import { supabase } from '../lib/supabase';

export interface VerseNote {
  id: string;
  user_id: string;
  verse_id: string;
  content: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  deleted: boolean;
}

class NotesService {
  async addNote(verseId: string, content: string): Promise<VerseNote> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('verse_notes')
      .upsert({
        verse_id: verseId,
        content: content,
        user_id: user.id,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,verse_id'
      })
      .select('*')
      .single();

    if (error) {
      throw error;
    }
    return data as VerseNote;
  }

  async updateNote(verseId: string, content: string): Promise<VerseNote> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('verse_notes')
      .update({
        content: content,
        updated_at: new Date().toISOString(),
      })
      .match({
        verse_id: verseId,
        user_id: user.id,
        deleted: false,
      })
      .select('*')
      .single();

    if (error) {
      throw error;
    }
    return data as VerseNote;
  }

  async deleteNote(verseId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('verse_notes')
      .update({ deleted: true })
      .match({
        verse_id: verseId,
        user_id: user.id,
        deleted: false,
      });

    if (error) {
      throw error;
    }
  }

  async getNote(verseId: string): Promise<VerseNote | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('verse_notes')
      .select('*')
      .match({
        verse_id: verseId,
        user_id: user.id,
        deleted: false,
      })
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data as VerseNote | null;
  }

  async getAllNotes(): Promise<VerseNote[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('verse_notes')
      .select('*')
      .match({
        user_id: user.id,
        deleted: false,
      })
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }
    return data as VerseNote[];
  }

  async getNotesForVerses(verseIds: string[]): Promise<VerseNote[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return [];
    }

    const { data, error } = await supabase
      .from('verse_notes')
      .select('*')
      .in('verse_id', verseIds)
      .eq('user_id', user.id)
      .eq('deleted', false);

    if (error) {
      throw new Error('Failed to load notes');
    }

    return data as VerseNote[];
  }
}

export const notesService = new NotesService();
